import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { STREAM_PLATFORMS, isPlaceholder } from "@/config/site";
import { LiveBadge } from "@/components/common/LiveBadge";
import { cn } from "@/lib/utils";

const buildEmbedUrl = (platform) => {
  if (isPlaceholder(platform.channel)) return null;
  if (typeof window === "undefined") return null;
  switch (platform.type) {
    case "twitch":
      return `https://player.twitch.tv/?channel=${platform.channel}&parent=${window.location.hostname}&muted=true`;
    case "youtube":
      return `https://www.youtube.com/embed/${platform.channel}`;
    case "kick":
      return `https://player.kick.com/${platform.channel}`;
    default:
      return null; // TikTok has no official LIVE embed method
  }
};

export const LiveStreamPlayer = ({ isLive = false }) => {
  const platforms = STREAM_PLATFORMS.filter((p) => p.enabled);
  const [activeId, setActiveId] = useState(platforms[0]?.id);
  const active = platforms.find((p) => p.id === activeId) || platforms[0];
  const embedUrl = isLive ? buildEmbedUrl(active) : null;

  const handleSelect = (platform) => {
    setActiveId(platform.id);
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "live_stream_platform_changed", { platform: platform.name });
    }
  };

  const handleVisitPlatform = () => {
    if (isPlaceholder(active.channelUrl)) {
      toast(`Add the ${active.name} channel URL in config/site.js`);
      return;
    }
    window.open(active.channelUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="live-stream-player" className="border border-border-dark bg-surface-dark">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-dark px-3 py-3">
        <div className="flex flex-wrap gap-1">
          {platforms.map((platform) => (
            <button
              key={platform.id}
              type="button"
              data-testid={`platform-tab-${platform.id}`}
              aria-pressed={active.id === platform.id}
              onClick={() => handleSelect(platform)}
              className={cn(
                "relative px-4 py-2 font-subheading text-sm font-semibold uppercase tracking-wide transition-colors",
                active.id === platform.id ? "text-white" : "text-white/40 hover:text-white/70"
              )}
            >
              {active.id === platform.id && (
                <motion.span
                  layoutId="platform-indicator"
                  className="absolute inset-0 -z-10 bg-brand-purple"
                  transition={{ type: "spring", duration: 0.5, bounce: 0.15 }}
                />
              )}
              <span className="relative z-10">{platform.name}</span>
            </button>
          ))}
        </div>
        <LiveBadge isLive={isLive} />
      </div>

      <div className="relative aspect-video w-full bg-black">
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            {embedUrl ? (
              <iframe
                data-testid="stream-player-iframe"
                src={embedUrl}
                title={`${active.name} live stream player`}
                allow="autoplay; fullscreen"
                allowFullScreen
                className="h-full w-full"
              />
            ) : (
              <div data-testid="stream-offline-state" className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <Radio className="h-8 w-8 text-white/30" aria-hidden="true" />
                <p className="font-heading text-2xl uppercase text-white">{isLive ? "Match information goes here" : "Nothing Live Right Now"}</p>
                <p className="max-w-sm text-sm text-white/50">
                  {active.name === "TikTok"
                    ? "TikTok LIVE can't be embedded on other websites — tap below to watch on TikTok once a link is added."
                    : "Upcoming live coverage information goes here."}
                </p>
                <button
                  type="button"
                  data-testid={`visit-platform-${active.id}`}
                  onClick={handleVisitPlatform}
                  className="mt-2 inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 font-subheading text-xs font-semibold uppercase tracking-wide text-white/70 transition-colors hover:border-brand-purple hover:text-white"
                >
                  Watch on {active.name} <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
